import React, { useState, useRef, useEffect } from 'react';
import {
  BarChart2, LineChart, AreaChart as AreaChartIcon, Table2,
  TrendingUp, Telescope, Minus, SortAsc, SortDesc, Filter,
  Download, FileText, Link2, Maximize2, Check,
  PenLine, type LucideIcon,
} from 'lucide-react';
import { ChartType, SortBy } from '../../context/DashboardContext';

export interface ChartToolbarProps {
  chartId:      string;
  // chart type switcher
  chartType:    ChartType;
  onChartType:  (t: ChartType) => void;
  // feature toggles
  trendLine:    boolean;
  forecast:     boolean;
  benchmark:    boolean;
  brush:        boolean;
  onToggle:     (f: 'trendLine' | 'forecast' | 'benchmark' | 'brush') => void;
  // sort
  sortBy:       SortBy;
  onSort:       (s: SortBy) => void;
  // top-N
  topN:         number | null;
  onTopN:       (n: number | null) => void;
  // export
  onExportPNG:  () => void;
  onExportCSV:  () => void;
  onShareLink:  () => void;
  shareCopied:  boolean;
  // fullscreen
  onFullscreen: () => void;
  // optional toggles for annotation mode
  annotationMode:    boolean;
  onAnnotationMode:  (v: boolean) => void;
  annotationCount:   number;
  // feature visibility gates (hide buttons that don't apply)
  enableForecast?:    boolean;
  enableDrillDown?:   boolean;
  enableAnnotations?: boolean;
  enableBenchmark?:   boolean;
  enableBrush?:       boolean;
  enableCompare?:     boolean;
}

const TYPES: { type: ChartType; Icon: LucideIcon; tip: string }[] = [
  { type: 'bar',   Icon: BarChart2,     tip: 'Bar chart'  },
  { type: 'line',  Icon: LineChart,     tip: 'Line chart' },
  { type: 'area',  Icon: AreaChartIcon, tip: 'Area chart' },
  { type: 'table', Icon: Table2,        tip: 'Table view' },
];

const SORTS: { value: SortBy; label: string }[] = [
  { value: 'value-desc', label: 'Value ↓ (highest first)' },
  { value: 'value-asc',  label: 'Value ↑ (lowest first)'  },
  { value: 'name-asc',   label: 'Name A–Z'                },
  { value: 'trend',      label: 'Trend (fastest growing)'  },
];

const TOP_NS = [5, 10, 20];

// ── Tiny tooltip wrapper ──────────────────────────────────────────────────────
function Tip({ label, children }: { label: string; children: React.ReactNode }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div style={{
          position: 'absolute', bottom: '110%', left: '50%', transform: 'translateX(-50%)',
          background: '#1a1a2e', color: '#fff', fontSize: 10, fontWeight: 500,
          padding: '3px 7px', borderRadius: 5, whiteSpace: 'nowrap', zIndex: 999,
          pointerEvents: 'none',
        }}>{label}</div>
      )}
    </div>
  );
}

// ── Icon button ───────────────────────────────────────────────────────────────
function IconBtn({
  onClick, active = false, danger = false, children, tip,
}: {
  onClick: () => void; active?: boolean; danger?: boolean;
  children: React.ReactNode; tip: string;
}) {
  return (
    <Tip label={tip}>
      <button
        onClick={onClick}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 26, height: 26, borderRadius: 6, border: 'none', cursor: 'pointer',
          background: active ? (danger ? '#FEF2F2' : 'rgba(176,125,26,0.08)') : 'transparent',
          color: active ? (danger ? '#DC2626' : '#b07d1a') : '#5a5a6e',
          transition: 'background 0.1s, color 0.1s',
        }}
        onMouseEnter={(e) => {
          if (!active) {
            e.currentTarget.style.background = danger ? '#FEF2F2' : 'rgba(0,0,0,0.04)';
            e.currentTarget.style.color = danger ? '#DC2626' : '#1a1a2e';
          }
        }}
        onMouseLeave={(e) => {
          if (!active) {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#5a5a6e';
          }
        }}
      >
        {children}
      </button>
    </Tip>
  );
}

// ── Divider ───────────────────────────────────────────────────────────────────
function Sep() {
  return <div style={{ width: 1, height: 16, background: 'rgba(0,0,0,0.1)', flexShrink: 0 }} />;
}

// ── Dropdown ─────────────────────────────────────────────────────────────────
function Dropdown({ trigger, children, width = 180 }: {
  trigger: React.ReactNode; children: React.ReactNode; width?: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div onClick={() => setOpen((o) => !o)}>{trigger}</div>
      {open && (
        <div style={{
          position: 'absolute', top: '110%', right: 0, zIndex: 1000,
          background: '#fff', border: '1px solid rgba(0,0,0,0.1)',
          borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          width, overflow: 'hidden',
        }}
          onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}

function MenuItem({ onClick, active, children }: {
  onClick: () => void; active?: boolean; children: React.ReactNode;
}) {
  return (
    <button onClick={onClick} style={{
      width: '100%', textAlign: 'left', background: active ? 'rgba(176,125,26,0.08)' : 'none',
      border: 'none', padding: '7px 12px', fontSize: 11.5, cursor: 'pointer',
      color: active ? '#b07d1a' : '#374151', display: 'flex', alignItems: 'center', gap: 6,
    }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#f7f5f2'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = active ? 'rgba(176,125,26,0.08)' : 'none'; }}
    >
      {active && <Check size={11} color="#b07d1a" />}
      {!active && <span style={{ width: 11, display: 'inline-block' }} />}
      {children}
    </button>
  );
}

// ── Main toolbar ──────────────────────────────────────────────────────────────

export default function ChartToolbar({
  chartType, onChartType,
  trendLine, forecast, benchmark, brush, onToggle,
  sortBy, onSort,
  topN, onTopN,
  onExportPNG, onExportCSV, onShareLink, shareCopied,
  onFullscreen,
  annotationMode, onAnnotationMode, annotationCount,
  enableForecast     = true,
  enableAnnotations  = true,
  enableBenchmark    = true,
  enableBrush        = true,
}: ChartToolbarProps) {

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap',
      padding: '2px 0 6px',
    }}>

      {/* ── Chart type switcher ─────────────────────────────── */}
      <div style={{ display: 'flex', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 7, overflow: 'hidden' }}>
        {TYPES.map(({ type, Icon, tip }) => (
          <Tip key={type} label={tip}>
            <button
              onClick={() => onChartType(type)}
              style={{
                width: 26, height: 26, display: 'flex', alignItems: 'center',
                justifyContent: 'center', border: 'none', cursor: 'pointer',
                background: chartType === type ? '#b07d1a' : '#fff',
                color: chartType === type ? '#fff' : '#5a5a6e',
                borderRight: type !== 'table' ? '1px solid rgba(0,0,0,0.1)' : 'none',
              }}>
              <Icon size={12} />
            </button>
          </Tip>
        ))}
      </div>

      <Sep />

      {/* ── Trend line ─────────────────────────────────────── */}
      <IconBtn onClick={() => onToggle('trendLine')} active={trendLine} tip="Trend line overlay">
        <TrendingUp size={13} />
      </IconBtn>

      {/* ── Forecast ──────────────────────────────────────── */}
      {enableForecast && (
        <IconBtn onClick={() => onToggle('forecast')} active={forecast} tip="3-period forecast">
          <Telescope size={13} />
        </IconBtn>
      )}

      {/* ── Benchmark line ────────────────────────────────── */}
      {enableBenchmark && (
        <IconBtn onClick={() => onToggle('benchmark')} active={benchmark} tip="Benchmark / target line">
          <Minus size={13} />
        </IconBtn>
      )}

      {/* ── Brush / range selector ────────────────────────── */}
      {enableBrush && (
        <IconBtn onClick={() => onToggle('brush')} active={brush} tip="Date range brush">
          <Filter size={13} />
        </IconBtn>
      )}

      <Sep />

      {/* ── Sort dropdown ─────────────────────────────────── */}
      <Dropdown
        width={210}
        trigger={
          <Tip label="Sort order">
            <button style={{
              display: 'flex', alignItems: 'center', gap: 3,
              padding: '4px 7px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.1)',
              background: sortBy !== 'value-desc' ? 'rgba(176,125,26,0.08)' : '#fff',
              color: sortBy !== 'value-desc' ? '#b07d1a' : '#5a5a6e',
              fontSize: 10.5, cursor: 'pointer', fontWeight: 500,
            }}>
              {sortBy.includes('asc') ? <SortAsc size={11} /> : <SortDesc size={11} />}
              Sort
            </button>
          </Tip>
        }>
        {SORTS.map((s) => (
          <MenuItem key={s.value} onClick={() => onSort(s.value)} active={sortBy === s.value}>
            {s.label}
          </MenuItem>
        ))}
      </Dropdown>

      {/* ── Top-N dropdown ────────────────────────────────── */}
      <Dropdown
        width={150}
        trigger={
          <Tip label="Show top / bottom N">
            <button style={{
              display: 'flex', alignItems: 'center', gap: 3,
              padding: '4px 7px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.1)',
              background: topN !== null ? 'rgba(176,125,26,0.08)' : '#fff',
              color: topN !== null ? '#b07d1a' : '#5a5a6e',
              fontSize: 10.5, cursor: 'pointer', fontWeight: 500,
            }}>
              Top {topN ?? 'All'}
            </button>
          </Tip>
        }>
        <MenuItem onClick={() => onTopN(null)} active={topN === null}>All</MenuItem>
        {TOP_NS.map((n) => (
          <MenuItem key={n} onClick={() => onTopN(n)} active={topN === n}>Top {n}</MenuItem>
        ))}
      </Dropdown>

      <Sep />

      {/* ── Annotations ───────────────────────────────────── */}
      {enableAnnotations && (
        <IconBtn
          onClick={() => onAnnotationMode(!annotationMode)}
          active={annotationMode}
          tip={annotationMode ? 'Exit annotation mode' : `Add annotation${annotationCount ? ` (${annotationCount})` : ''}`}
        >
          <PenLine size={13} />
        </IconBtn>
      )}

      {/* ── Export PNG ────────────────────────────────────── */}
      <IconBtn onClick={onExportPNG} tip="Export as PNG">
        <Download size={13} />
      </IconBtn>

      {/* ── Export CSV ────────────────────────────────────── */}
      <IconBtn onClick={onExportCSV} tip="Export data as CSV">
        <FileText size={13} />
      </IconBtn>

      {/* ── Share link ────────────────────────────────────── */}
      <IconBtn onClick={onShareLink} active={shareCopied} tip={shareCopied ? 'Link copied!' : 'Copy share link'}>
        {shareCopied ? <Check size={13} /> : <Link2 size={13} />}
      </IconBtn>

      <Sep />

      {/* ── Fullscreen ────────────────────────────────────── */}
      <IconBtn onClick={onFullscreen} tip="Expand fullscreen">
        <Maximize2 size={13} />
      </IconBtn>
    </div>
  );
}
